import type { CatalogItem, Category, ContentKind, Source } from '@viva/core';
import { isVivaError } from '@viva/core';
import type { PagedItems } from '@viva/ui';
import { usePagedItems, VirtualGrid, VirtualList } from '@viva/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CategorySlice } from '../category-pages';
import { listAcross } from '../category-pages';
import { categoryTags } from '../category-tags';
import { ErrorMessage } from '../components/ErrorMessage';
import { FrontPage } from './FrontPage';
import { useChannelList } from '../channel-memory';
import { ChannelRow, PosterCard } from '../components/ItemViews';
import { hasSavedFilter, matchesFilter, TagFilterBar, useTagFilter } from '../components/TagFilters';
import { useCatalog } from '../state/catalog-context';
import { usePlay } from '../state/playback-context';
import { uhdHidden, uhdReason } from '../uhd-access';
import { languageName, t, useLanguage } from '../i18n';
import type { MessageKey } from '../i18n/en';

const TITLES: Record<ContentKind, MessageKey> = { live: 'nav.live', movie: 'nav.movies', series: 'nav.series' };
const ALL = '\u0000all';
const RECENT = '\u0000recent';
/** Live TV: channels watched last, and the viewer's favorites (this device). */
const WATCHED = '\u0000watched';
const FAVORITES = '\u0000favorites';
/** Movies and Series open on rows: Continue watching, Recently added, then categories. */
const FRONT = '\u0000front';
/** How many of the newest movies or series "Recently added" shows. */
const RECENT_SHOWN = 100;
/** Fetched before the language or country filter, so a filtered list still fills up. */
const RECENT_FETCHED = 300;
/** Matches the phone layout breakpoint in styles.css. */
const PHONE_QUERY = '(max-width: 760px)';

interface BrowseProps {
  kind: ContentKind;
  sourceId: string | null;
  onSourceChange: (id: string) => void;
}

export function BrowseScreen({ kind, sourceId, onSourceChange }: BrowseProps) {
  useLanguage();
  const { sources } = useCatalog();
  // The chosen source, else the most recently added or updated one.
  const source = sources?.find((candidate) => candidate.id === sourceId) ?? latestSource(sources) ?? null;

  if (sources === null) {
    return (
      <section className="screen">
        <h1>{t(TITLES[kind])}</h1>
        <p className="muted">{t('common.loading')}</p>
      </section>
    );
  }
  if (sources.length === 0) {
    return (
      <section className="screen">
        <h1>{t(TITLES[kind])}</h1>
        <p className="muted">{t('browse.loadingChannels')}</p>
      </section>
    );
  }
  return (
    <section className="screen screen--browse">
      <header className="screen__header">
        <h1>{t(TITLES[kind])}</h1>
        {sources.length > 1 ? (
          <select className="select" value={source?.id ?? ''} onChange={(event) => onSourceChange(event.target.value)} aria-label={t('browse.source')}>
            {sources.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
        ) : null}
      </header>
      {source ? <Browser key={`${source.id}:${syncKey(source, kind)}:${kind}`} source={source} kind={kind} /> : null}
    </section>
  );
}

/**
 * Changes when this kind's content changes: a new sync, or the kind finishing
 * a first load. Live TV keeps its place while movies and series arrive.
 */
function syncKey(source: Source, kind: ContentKind): string {
  const sync = source.lastSync;
  if (!sync) return '';
  return `${sync.finishedAt - sync.durationMs}${sync.pending?.includes(kind) ? ':pending' : ''}`;
}

function latestSource(sources: Source[] | null): Source | undefined {
  if (!sources || sources.length === 0) return undefined;
  const touched = (source: Source) => Math.max(source.createdAt, source.lastSync?.finishedAt ?? 0);
  return sources.reduce((latest, candidate) => (touched(candidate) > touched(latest) ? candidate : latest));
}

function Browser({ source, kind }: { source: Source; kind: ContentKind }) {
  const { catalog } = useCatalog();
  const [categories, setCategories] = useState<Category[] | null>(null);
  const start = kind === 'live' ? ALL : FRONT;
  const [selected, setSelected] = useState(start);
  const [error, setError] = useState<unknown>(null);
  const [recent, setRecent] = useState<CatalogItem[]>([]);

  useEffect(() => {
    let active = true;
    catalog.listCategories(source.id, kind).then(
      (list) => active && setCategories(list),
      (cause: unknown) => active && setError(cause),
    );
    // Movies and series only; sources without added dates (M3U) get none, and the entry stays hidden.
    if (kind !== 'live') {
      catalog.listRecent(source.id, kind, RECENT_FETCHED).then(
        (list) => active && setRecent(list),
        () => undefined,
      );
    }
    return () => {
      active = false;
    };
  }, [catalog, source.id, kind]);

  const tagged = useMemo(() => (categories ?? []).map((category) => ({ category, tags: categoryTags(category.name), itemCount: category.itemCount })), [categories]);
  const tagFilter = useTagFilter(kind, tagged);
  const { active, filtered } = tagFilter;
  const shown = useMemo(
    () => tagged.filter(({ tags }) => matchesFilter(tags, active)).map(({ category }) => category),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `active` is rebuilt each render from these values
    [tagged, active.language, active.country],
  );
  const slices = useMemo<CategorySlice[] | null>(() => (filtered ? shown.map(({ key, itemCount }) => ({ key, itemCount })) : null), [filtered, shown]);
  const shownRecent = useMemo(() => {
    const keys = filtered ? new Set(shown.map((category) => category.key)) : null;
    return (keys ? recent.filter((item) => keys.has(item.categoryKey)) : recent).slice(0, RECENT_SHOWN);
  }, [recent, filtered, shown]);
  // The filter can empty "Recently added" while it is selected: fall back to the start.
  const watchedChannels = useChannelList(source.id, 'recent');
  const favoriteChannels = useChannelList(source.id, 'favorites');
  const emptied =
    (selected === RECENT && shownRecent.length === 0) || (selected === WATCHED && watchedChannels.length === 0) || (selected === FAVORITES && favoriteChannels.length === 0);
  const current = emptied ? start : selected;

  const total = source.lastSync?.counts[kind] ?? 0;
  if (error && isVivaError(error)) return <ErrorMessage error={error} />;
  if (source.lastSync?.pending?.includes(kind)) return <PendingKind kind={kind} source={source} />;
  if (total === 0) return <p className="muted">{t(`browse.empty.${kind}`)}</p>;
  const shownTotal = slices ? slices.reduce((sum, slice) => sum + slice.itemCount, 0) : total;
  const allLabel = filtered ? t('browse.allFiltered', { filter: [active.country, active.language && languageName(active.language)].filter(Boolean).join(' · ') }) : t('search.all');

  return (
    <>
      {kind !== 'live' && uhdHidden() ? <p className="muted browse__uhd-note">{t(uhdReason() === 'relay' ? 'browse.uhdPaused' : 'browse.uhdHidden')}</p> : null}
      <TagFilterBar state={tagFilter} onChange={() => setSelected(start)} />
      <div className="browser">
        <nav className="category-list" aria-label={t('browse.categories')}>
          {kind === 'live' && favoriteChannels.length > 0 ? (
            <CategoryButton name={`★ ${t('favorites.title')} (${favoriteChannels.length})`} active={current === FAVORITES} onSelect={() => setSelected(FAVORITES)} />
          ) : null}
          {kind === 'live' && watchedChannels.length > 0 ? (
            <CategoryButton name={t('browse.watched')} active={current === WATCHED} onSelect={() => setSelected(WATCHED)} />
          ) : null}
          {kind !== 'live' ? <CategoryButton name={t('browse.front')} active={current === FRONT} onSelect={() => setSelected(FRONT)} /> : null}
          {shownRecent.length > 0 ? (
            <CategoryButton name={t('browse.recent')} active={current === RECENT} onSelect={() => setSelected(RECENT)} />
          ) : null}
          <CategoryButton name={`${allLabel} (${shownTotal.toLocaleString()})`} active={current === ALL} onSelect={() => setSelected(ALL)} />
          {(categories ? shown : []).map((category) => (
            <CategoryButton
              key={category.key}
              name={`${category.name || t('browse.other')} (${category.itemCount.toLocaleString()})`}
              active={current === category.key}
              onSelect={() => setSelected(category.key)}
            />
          ))}
        </nav>
        {/* With a remembered filter, wait for the categories rather than flash the unfiltered list. */}
        {categories === null && hasSavedFilter(kind) ? null : current === FRONT && kind !== 'live' ? (
          <FrontPage
            source={source}
            kind={kind}
            recent={shownRecent}
            categories={categories ? shown : []}
            onSeeRecent={() => setSelected(RECENT)}
            onSeeCategory={setSelected}
          />
        ) : current === RECENT ? (
          <FixedItemsPane source={source} kind={kind} items={shownRecent} />
        ) : current === WATCHED || current === FAVORITES ? (
          <FixedItemsPane source={source} kind={kind} items={current === WATCHED ? watchedChannels : favoriteChannels} />
        ) : (
          <ItemsPane source={source} kind={kind} categoryKey={current === ALL ? null : current} slices={current === ALL ? slices : null} />
        )}
      </div>
    </>
  );
}

/** A kind still loading in the background on a first load; Live TV is usually ready by then. */
function PendingKind({ kind, source }: { kind: ContentKind; source: Source }) {
  const { task, refreshSource } = useCatalog();
  const live = source.lastSync?.pending?.includes('live') ? null : source.lastSync?.counts.live ?? 0;
  if (!task) {
    // Not loading anymore: the first load stopped before reaching this kind.
    return (
      <div className="pending-kind">
        <p className="muted">{t('browse.unfinished', { kind: t(TITLES[kind]) })}</p>
        <button type="button" className="button" onClick={() => void refreshSource(source)}>
          {t(`browse.load.${kind}`)}
        </button>
      </div>
    );
  }
  return (
    <p className="muted">
      {t(`browse.loading.${kind}`)} {live ? t('browse.meanwhile', { count: live.toLocaleString() }) : ''}
    </p>
  );
}

function CategoryButton({ name, active, onSelect }: { name: string; active: boolean; onSelect: () => void }) {
  return (
    <button type="button" className="category-list__item" aria-current={active} onClick={onSelect}>
      {name}
    </button>
  );
}

function ItemsPane({ source, kind, categoryKey, slices }: { source: Source; kind: ContentKind; categoryKey: string | null; slices: CategorySlice[] | null }) {
  const { catalog } = useCatalog();
  const fetchPage = useCallback(
    (offset: number, limit: number) =>
      slices ? listAcross(catalog, source.id, kind, slices, offset, limit) : catalog.listItems({ sourceId: source.id, kind, categoryKey, offset, limit }),
    [catalog, source.id, kind, categoryKey, slices],
  );
  const sliceKey = slices ? slices.map((slice) => slice.key).join(',') : '';
  const items = usePagedItems<CatalogItem>(`${source.id}:${kind}:${categoryKey ?? ''}:${sliceKey}`, fetchPage, 120);
  const play = usePlay();
  const phone = useMediaQuery(PHONE_QUERY);
  const open = (item: CatalogItem, index: number) =>
    play({ item, list: { sourceId: source.id, kind, categoryKey, categories: slices ?? undefined, index, total: items.total ?? 0 } });

  if (items.error && isVivaError(items.error)) return <ErrorMessage error={items.error} />;
  if (kind === 'live') {
    return <VirtualList className="items" items={items} rowHeight={64} renderRow={(item, index) => <ChannelRow item={item} index={index} onOpen={(opened) => open(opened, index)} />} />;
  }
  return <PosterGrid items={items} phone={phone} onOpen={open} />;
}

/** A short list already in memory, such as "Recently added", shown in the same poster grid. */
function FixedItemsPane({ source, kind, items }: { source: Source; kind: ContentKind; items: CatalogItem[] }) {
  const fetchPage = useCallback(
    (offset: number, limit: number) => Promise.resolve({ items: items.slice(offset, offset + limit), offset, total: items.length }),
    [items],
  );
  const paged = usePagedItems<CatalogItem>(`${source.id}:${kind}:fixed:${items.map((item) => item.key).join(',')}`, fetchPage, 120);
  const play = usePlay();
  const phone = useMediaQuery(PHONE_QUERY);
  if (kind === 'live') {
    // ▲ ▼ in the player zap through this list.
    return (
      <VirtualList
        className="items"
        items={paged}
        rowHeight={64}
        renderRow={(item, index) => <ChannelRow item={item} index={index} onOpen={(opened) => play({ item: opened, queue: { items, index } })} />}
      />
    );
  }
  return <PosterGrid items={paged} phone={phone} onOpen={(item) => play({ item })} />;
}

function PosterGrid({ items, phone, onOpen }: { items: PagedItems<CatalogItem>; phone: boolean; onOpen: (item: CatalogItem, index: number) => void }) {
  return (
    <VirtualGrid
      className="items items--grid"
      items={items}
      minCellWidth={phone ? 100 : 150}
      aspect={1.5}
      captionHeight={phone ? 34 : 40}
      gap={phone ? 10 : 16}
      renderCell={(item, index) => <PosterCard item={item} onOpen={(opened) => onOpen(opened, index)} />}
    />
  );
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof matchMedia === 'function' && matchMedia(query).matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return undefined;
    const list = matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    // addListener: Chromium 68 predates addEventListener on MediaQueryList.
    list.addListener(update);
    return () => list.removeListener(update);
  }, [query]);
  return matches;
}
